import { useEffect, useState } from 'react';
import { api } from '@/utils/api';

export interface ThemeSettings {
    theme_primary_color: string | null;
    theme_accent_color: string | null;
    theme_mode: string | null;
    site_name: string | null;
    site_tagline: string | null;
    footer_text: string | null;
    maintenance_mode: boolean | null;
}

const DEFAULTS: ThemeSettings = {
    theme_primary_color: null,
    theme_accent_color: null,
    theme_mode: 'auto',
    site_name: null,
    site_tagline: null,
    footer_text: null,
    maintenance_mode: false,
};

/**
 * Fetch public theme/content settings once and apply them as CSS custom
 * properties on :root (--primary-color, --accent-color) plus a data-theme
 * attribute for light/dark/auto mode.
 */
export function useThemeSettings() {
    const [settings, setSettings] = useState<ThemeSettings>(DEFAULTS);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const apply = (s: ThemeSettings) => {
            const root = document.documentElement;

            if (s.theme_primary_color) {
                root.style.setProperty('--primary-color', s.theme_primary_color);
            }
            if (s.theme_accent_color) {
                root.style.setProperty('--accent-color', s.theme_accent_color);
            }

            // daisyUI theme handling: use custom theme names.
            // Only override data-theme if the admin explicitly set a mode (not 'auto').
            const mode = s.theme_mode || 'auto';
            if (mode === 'light') {
                root.setAttribute('data-theme', 'everythingsliveLight');
            } else if (mode === 'dark') {
                root.setAttribute('data-theme', 'everythingslive');
            }
            // 'auto' → leave data-theme as-is (the inline script / uiStore already set it).

            if (s.site_name) {
                document.title = s.site_name;
            }
        };

        api.get('/v1/public/settings')
            .then((response) => {
                if (cancelled) return;
                const data = response.data?.settings || {};
                const merged: ThemeSettings = {
                    ...DEFAULTS,
                    theme_primary_color: data.theme_primary_color ?? null,
                    theme_accent_color: data.theme_accent_color ?? null,
                    theme_mode: data.theme_mode ?? 'auto',
                    site_name: data.site_name ?? null,
                    site_tagline: data.site_tagline ?? null,
                    footer_text: data.footer_text ?? null,
                    maintenance_mode: data.maintenance_mode ?? false,
                };
                setSettings(merged);
                apply(merged);
            })
            .catch(() => {
                // Non-fatal: fall back to defaults if the endpoint is unavailable.
            })
            .finally(() => {
                if (!cancelled) setLoaded(true);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    return { settings, loaded };
}
